"use client";

import { useEffect, useRef, useState } from "react";
import Modal from "./Modal";

interface NewFolderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the trimmed, non-empty folder name when the user confirms. */
  onCreate: (name: string) => void;
}

/**
 * Small naming dialog used to create a folder by name (e.g. when moving
 * multi-selected chats into a brand-new folder). Auto-focuses the input,
 * Enter confirms, Escape/overlay closes.
 */
export default function NewFolderDialog({ isOpen, onClose, onCreate }: NewFolderDialogProps) {
  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName("");
      // Focus the input (not the modal's close button). Runs before Modal's
      // own 50ms auto-focus, which then no-ops because focus is already inside.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate(trimmed);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New Folder">
      <input
        ref={inputRef}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
        placeholder="Folder name"
        style={{
          width: "100%",
          padding: "10px 12px",
          fontSize: "14px",
          background: "var(--surface-2, var(--surface))",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          color: "var(--text-primary)",
          outline: "none",
        }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px" }}>
        <button className="btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn-primary" onClick={submit} disabled={!name.trim()}>
          Create
        </button>
      </div>
    </Modal>
  );
}
